import { forwardRef, useContext, useEffect, useRef, useState } from "react";
import { OTPInputContext } from "input-otp";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Eye, EyeOff, KeyRound, Loader2, Mail, ShieldCheck, Calculator, Server, RadioTower, Volume2, VolumeX } from "lucide-react";
import { InputOTP, InputOTPGroup } from "@/components/ui/input-otp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/common/Brand";

/** Masked OTP slot: shows a bullet when a digit is present, otherwise empty. */
const MaskedOTPSlot = forwardRef(({ index, reveal = false, className, ...props }, ref) => {
  const ctx = useContext(OTPInputContext);
  const { char, hasFakeCaret, isActive } = ctx.slots[index] || { char: null, hasFakeCaret: false, isActive: false };
  return (
    <div
      ref={ref}
      className={cn(
        "relative flex h-12 w-11 sm:w-12 items-center justify-center rounded-lg border border-[hsl(220_10%_20%)] bg-[hsl(220_12%_9%)] text-lg transition-all",
        isActive && "z-10 ring-1 ring-primary border-primary/60",
        className
      )}
      {...props}
    >
      {char ? (
        reveal ? (
          <span className="num text-foreground">{char}</span>
        ) : (
          <span className="text-foreground" aria-hidden>
            {"\u2022"}
          </span>
        )
      ) : null}
      {hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-5 w-px animate-caret-blink bg-primary/80 duration-1000" />
        </div>
      )}
    </div>
  );
});
MaskedOTPSlot.displayName = "MaskedOTPSlot";

const BG =
  "https://images.unsplash.com/photo-1634549709262-508c47d4c229?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600";
const PROMO_VIDEO =
  "https://customer-assets-lqy194kg.emergentagent.net/job_tools-bisnis--5ip0rzbh/artifacts/337d1b2d663c19cf_kalkulatoreddy.com%20Reels%20v1.mp4";

const ADMIN_EMAIL = "eddy.yansen@gmail.com";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [step, setStep] = useState("email");
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [pinLength, setPinLength] = useState(6);
  const [method, setMethod] = useState("email");
  const [masked, setMasked] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [shake, setShake] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [info, setInfo] = useState("");
  const [reveal, setReveal] = useState(false);
  const [muted, setMuted] = useState(true);
  const timer = useRef(null);
  const videoRef = useRef(null);

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted) v.play().catch(() => {});
  };

  useEffect(() => {
    if (countdown <= 0) return;
    timer.current = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer.current);
  }, [countdown]);

  const fail = (msg) => {
    setError(msg);
    setShake(true);
    setTimeout(() => setShake(false), 450);
  };

  const requestPin = async (e) => {
    e && e.preventDefault();
    setError("");
    setInfo("");
    const em = email.trim().toLowerCase();
    if (!em || !em.includes("@")) return fail("Please enter a valid email address.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/request-pin", { email: em });
      setMethod(data.method);
      setPinLength(data.pin_length);
      setMasked(data.masked_email);
      setInfo(data.message);
      setPin("");
      setReveal(false);
      setStep("pin");
      if (data.method === "email") setCountdown(data.resend_in || 60);
    } catch (err) {
      fail(errMsg(err, "Unable to send PIN. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const haveCode = () => {
    setError("");
    const em = email.trim().toLowerCase();
    if (!em || !em.includes("@")) return fail("Enter your email first, then continue with your PIN.");
    const isAdmin = em === ADMIN_EMAIL;
    setMethod(isAdmin ? "admin_pin" : "existing");
    setPinLength(isAdmin ? 4 : 6);
    setMasked(em);
    setInfo(isAdmin ? "Enter your admin PIN to continue." : "Enter the PIN you received.");
    setPin("");
    setReveal(false);
    setStep("pin");
  };

  const verify = async (value) => {
    const code = value ?? pin;
    if (code.length < pinLength) return fail(`Please enter all ${pinLength} digits.`);
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/verify-pin", { email: email.trim().toLowerCase(), pin: code });
      login(data.token, data.user);
      nav(loc.state?.from || "/dashboard", { replace: true });
    } catch (err) {
      setPin("");
      fail(errMsg(err, "Invalid PIN. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative z-[1] min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      {/* Promo video panel (desktop) — video tampil utuh di depan, teks di bawah */}
      <div className="hidden lg:flex flex-col overflow-hidden border-r border-border bg-[hsl(220_14%_4%)]">
        <div className="px-10 pt-8 pb-5 flex items-start justify-between gap-4">
          <Logo />
          <button
            type="button"
            onClick={toggleMute}
            aria-pressed={!muted}
            aria-label={muted ? "Unmute video" : "Mute video"}
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--hairline)] bg-[hsl(220_14%_6%)] text-foreground/90 hover:text-primary hover:border-primary/60 transition-colors focus-visible:ring-2 focus-visible:ring-primary"
            data-testid="login-promo-mute-toggle"
          >
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
        </div>

        <div className="px-10">
          <div className="relative w-full aspect-video rounded-xl overflow-hidden border border-[color:var(--hairline)] bg-black elev-2">
            <video
              ref={videoRef}
              src={PROMO_VIDEO}
              poster={BG}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              className="absolute inset-0 h-full w-full object-cover"
              data-testid="login-promo-video"
            />
          </div>
        </div>

        <div className="px-10 pt-6 pb-4 max-w-xl">
          <div className="eyebrow mb-3">Private business toolkit</div>
          <h1 className="font-display text-3xl xl:text-4xl leading-[1.1] text-foreground">
            Precise numbers for <span className="text-primary">everyday</span> business decisions.
          </h1>
          <p className="mt-3 text-[13px] text-[hsl(45_15%_78%)] leading-relaxed">
            Tax, hosting and network cost calculators in one secure workspace — with AI recommendations, saved scenarios and team sharing.
          </p>
          <div className="mt-4 grid grid-cols-3 gap-3">
            {[
              { icon: Calculator, t: "Tax PPh 21 · 23 · 4(2)" },
              { icon: Server, t: "Hosting / VPS COGS" },
              { icon: RadioTower, t: "ISP network COGS" },
            ].map((x) => (
              <div key={x.t} className="rounded-xl border border-[color:var(--hairline)] bg-[hsl(220_14%_6%)] p-2.5">
                <x.icon className="h-4 w-4 text-primary" />
                <div className="text-[11px] mt-1.5 text-[hsl(45_15%_84%)] leading-snug">{x.t}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-auto px-10 py-5 text-xs text-muted-foreground border-t border-border/60">
          Copyright © {new Date().getFullYear()} <span className="text-[hsl(43_60%_64%)]">Eddy Yansen</span>. All rights reserved.
        </div>
      </div>

      {/* Form panel */}
      <div className="flex flex-col">
        {/* Mobile promo video — tampil utuh di atas form */}
        <div className="lg:hidden px-5 pt-5 pb-3">
          <Logo />
          <div className="mt-4 relative w-full aspect-video rounded-xl overflow-hidden border border-[color:var(--hairline)] bg-black">
            <video
              src={PROMO_VIDEO}
              poster={BG}
              autoPlay
              muted
              loop
              playsInline
              preload="metadata"
              className="absolute inset-0 h-full w-full object-cover"
              data-testid="login-promo-video-mobile"
            />
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className={`w-full max-w-[420px] fade-up ${shake ? "shake" : ""}`}>
            <div className="rounded-2xl border border-[color:var(--hairline)] bg-card p-8 elev-2" data-testid="login-card">
              {step === "email" ? (
                <form onSubmit={requestPin} className="space-y-6">
                  <div>
                    <div className="h-11 w-11 rounded-xl border border-[color:var(--hairline)] bg-[hsl(43_30%_9%)] flex items-center justify-center mb-5">
                      <Mail className="h-5 w-5 text-primary" />
                    </div>
                    <h2 className="font-display text-3xl">Sign in</h2>
                    <p className="text-sm text-muted-foreground mt-2">Enter your work email to receive a one-time PIN.</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-xs">Email address</Label>
                    <Input
                      id="email"
                      type="email"
                      autoComplete="email"
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@thrive.co.id"
                      className="h-11 bg-[hsl(220_12%_8%)] border-[hsl(220_10%_18%)]"
                      data-testid="login-email-input"
                    />
                  </div>
                  {error && (
                    <div role="alert" aria-live="polite" className="rounded-lg border border-[hsl(0_60%_40%)] bg-[hsl(0_40%_10%)] px-3 py-2 text-sm text-[hsl(0_80%_80%)]" data-testid="login-error">
                      {error}
                    </div>
                  )}
                  <Button type="submit" disabled={loading} className="w-full h-11 press text-[15px]" data-testid="login-send-pin-button">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />} Send PIN
                  </Button>
                  <button
                    type="button"
                    onClick={haveCode}
                    className="w-full text-xs text-muted-foreground hover:text-primary"
                    data-testid="login-have-pin-link"
                  >
                    Already have a PIN? Enter it here
                  </button>
                  <p className="text-[11px] text-muted-foreground leading-relaxed border-t border-border pt-4">
                    Access is limited to authorized company emails. Every sign-in uses a fresh PIN sent to your inbox.
                  </p>
                </form>
              ) : (
                <div className="space-y-6">
                  <div>
                    <div className="h-11 w-11 rounded-xl border border-[color:var(--hairline)] bg-[hsl(43_30%_9%)] flex items-center justify-center mb-5">
                      {method === "admin_pin" ? <ShieldCheck className="h-5 w-5 text-primary" /> : <KeyRound className="h-5 w-5 text-primary" />}
                    </div>
                    <h2 className="font-display text-3xl">{method === "admin_pin" ? "Admin PIN" : "Verify PIN"}</h2>
                    <p className="text-sm text-muted-foreground mt-2" aria-live="polite" data-testid="login-pin-info">
                      {method === "email" ? (
                        <>
                          We sent a PIN to <span className="text-foreground">{masked}</span>. Enter it below to continue.
                        </>
                      ) : (
                        <>
                          {info} <span className="text-foreground">{masked}</span>
                        </>
                      )}
                    </p>
                  </div>
                  <div className="flex flex-col items-center gap-2">
                    <InputOTP
                      maxLength={pinLength}
                      value={pin}
                      onChange={(v) => {
                        setPin(v.replace(/\D/g, ""));
                        setError("");
                      }}
                      onComplete={(v) => verify(v)}
                      autoFocus
                      inputMode="numeric"
                      pattern="^[0-9]*$"
                      data-testid="login-pin-otp-input"
                    >
                      <InputOTPGroup className="gap-2">
                        {Array.from({ length: pinLength }).map((_, i) => (
                          <MaskedOTPSlot key={i} index={i} reveal={reveal} data-testid={`login-pin-slot-${i}`} />
                        ))}
                      </InputOTPGroup>
                    </InputOTP>
                    <button
                      type="button"
                      onClick={() => setReveal((v) => !v)}
                      className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary"
                      aria-pressed={reveal}
                      data-testid="login-pin-reveal-toggle"
                    >
                      {reveal ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                      {reveal ? "Hide PIN" : "Show PIN"}
                    </button>
                  </div>
                  {error && (
                    <div role="alert" aria-live="polite" className="rounded-lg border border-[hsl(0_60%_40%)] bg-[hsl(0_40%_10%)] px-3 py-2 text-sm text-[hsl(0_80%_80%)]" data-testid="login-error">
                      {error}
                    </div>
                  )}
                  <Button onClick={() => verify()} disabled={loading || pin.length < pinLength} className="w-full h-11 press text-[15px]" data-testid="login-verify-pin-button">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Verify
                  </Button>
                  <div className="flex items-center justify-between text-xs">
                    <button
                      onClick={() => {
                        setStep("email");
                        setError("");
                        setPin("");
                      }}
                      className="inline-flex items-center gap-1 text-muted-foreground hover:text-foreground"
                      data-testid="login-change-email-button"
                    >
                      <ArrowLeft className="h-3.5 w-3.5" /> Use a different email
                    </button>
                    {method !== "admin_pin" &&
                      (countdown > 0 ? (
                        <span className="text-muted-foreground num" data-testid="login-resend-countdown">
                          You can resend in {countdown}s
                        </span>
                      ) : (
                        <button onClick={requestPin} disabled={loading} className="text-primary hover:underline" data-testid="login-resend-pin-button">
                          Resend PIN
                        </button>
                      ))}
                  </div>
                  {method === "email" && (
                    <div className="rounded-lg border border-[color:var(--hairline)] bg-[hsl(43_30%_7%)] px-3 py-2.5 text-[11px] leading-relaxed text-[hsl(45_20%_75%)]" data-testid="login-pin-help-note">
                      Tidak menerima email dalam 2 menit? Cek folder <b className="text-foreground">Spam/Junk</b>, atau minta admin membuat <b className="text-foreground">PIN manual</b> untuk Anda (lewat WhatsApp/chat).
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="lg:hidden text-center text-xs text-muted-foreground pb-6">
          Copyright © {new Date().getFullYear()} Eddy Yansen
        </div>
      </div>
    </div>
  );
}
